import {
  GRANTVESTMENT_STATUS,
  GrantVestment,
  GrantVestmentRequest,
  LegalComplianceChecklist,
} from "types/grant-vestment";
import apiHttpClient from "libs/api-http-client";
import { PaginatedEntityList } from "types/pagination";
import { GrantVestmentInteraction } from "types/grant-vestment-interaction";
import { GrantVestmentFollower } from "types/grant-vestment-follower";
import { FundGvDistribution } from "types/fund-gv-distribution";
const getOrderBy = (value: string) => {
  switch (value) {
    case "Newest":
      return { createdAt: "DESC" };
    case "End date":
      return { deadline: "DESC" };
    case "Most funded":
      return { raisedAmount: "DESC" };
    case "Least funded":
      return { raisedAmount: "ASC" };
    default:
      return { createdAt: "DESC" };
  }
};

const prepareQuery = (filters: any) => {
  let query: any;
  if (filters?.keyword !== "") {
    query = { ...query, keyword: filters?.keyword };
  }

  if (filters?.category !== 0) {
    query = { ...query, category: filters?.category };
  }
  if (filters?.show) {
    if (filters?.show !== "All") {
      query = { ...query, status: [filters?.show] };
    } else {
      query = {
        ...query,
        status: [
          GRANTVESTMENT_STATUS.CLOSED,
          GRANTVESTMENT_STATUS.RAISING,
          GRANTVESTMENT_STATUS.PROMOTING,
        ],
      };
    }
  } else {
    query = {
      ...query,
      status: [GRANTVESTMENT_STATUS.CLOSED, GRANTVESTMENT_STATUS.RAISING],
    };
  }
  query = { ...query, sort: getOrderBy(filters?.sort) };
  return query;
};
export const getGrantVestmentAPI = (
  grantVestmentId: number
): Promise<GrantVestment> =>
  apiHttpClient.get(`/grant-vestments/${grantVestmentId}`);

export const makeGrantVestmentRequestAPI = (
  grantVestmentId: number,
  data: {
    requestedAmount: number;
    fundId: number;
    legalComplianceChecklist?: LegalComplianceChecklist;
  }
): Promise<unknown> =>
  apiHttpClient.post(`/grant-vestments/${grantVestmentId}/requests`, data);

export const getGrantVestmentRequestAPI = (
  grantVestmentRequestId: number
): Promise<GrantVestmentRequest> =>
  apiHttpClient.get(`/grant-vestment-requests/${grantVestmentRequestId}`);
export const cancelGrantVestmentRequestAPI = (
  grantVestmentRequestId: number
): Promise<GrantVestmentRequest> =>
  apiHttpClient.patch(
    `/grant-vestment-requests/${grantVestmentRequestId}/cancel`
  );
export interface getGrantVestmentsQuery {
  offset?: number;
  limit?: number;
  filters?: any;
}
export const getGrantVestmentsAPI = (
  query: getGrantVestmentsQuery
): Promise<PaginatedEntityList<GrantVestment>> => {
  const filters = prepareQuery(query.filters);
  return apiHttpClient.get(`/grant-vestments`, {
    params: {
      offset: query.offset,
      limit: query.limit,
      searchTerm: filters?.keyword,
      filter: {
        categoryId: filters?.category,
        status: filters?.status,
      },
      orderBy: filters.sort,
    },
  });
};
export interface GetGrantVestmentRequestsQuery {
  offset?: number;
  limit?: number;
  filter: {
    fundAccountId: number;
  };
}
export interface GetGrantVestmentDistributionQuery {
  offset?: number;
  limit?: number;
  filter: {
    fundAccountId: number;
    grantVestmentId: number;
  };
}

export const getGrantVestmentRequestsByFundAPI = (
  grantVestmentId: number,
  query: GetGrantVestmentRequestsQuery
): Promise<PaginatedEntityList<GrantVestmentRequest>> => {
  return apiHttpClient.get(`/grant-vestments/${grantVestmentId}/requests`, {
    params: {
      offset: query.offset,
      limit: query.limit,
      filter: {
        fundAccountId: query.filter.fundAccountId,
      },
    },
  });
};

export const getGrantVestmentDistributionByFundAPI = (
  query: GetGrantVestmentDistributionQuery
): Promise<PaginatedEntityList<FundGvDistribution>> => {
  return apiHttpClient.get(`fund-gv-distribution/list`, {
    params: {
      offset: query.offset,
      limit: query.limit,
      filter: {
        fundAccountId: query.filter.fundAccountId,
        grantVestmentId: query.filter.grantVestmentId,
      },
    },
  });
};

export interface GrantvestmentInteractionInput {
  grantvestmentId: number;
  sub: string;
  liked: boolean;
}

export interface GrantvestmentFollowerInput {
  grantvestmentId: number;
  sub: string;
}
export const addGrantvestmentInteractionAPI = (
  grantvestmentInteractionInput: GrantvestmentInteractionInput
): Promise<unknown> =>
  apiHttpClient.post(
    `grantvestment-interaction`,
    grantvestmentInteractionInput
  );

export const addGrantvestmentFollowerAPI = (
  grantvestmentInteractionInput: GrantvestmentFollowerInput
): Promise<unknown> =>
  apiHttpClient.post(`grantvestment-follower`, grantvestmentInteractionInput);

export interface GetGrantvestmentInteraction {
  offset?: number;
  limit?: number;
  filter: {
    grantvestmentId: number;
    userId: number;
  };
}
export const getGrantvestmentInteractionAPI = (
  getGrantvestmentInteractionInput: GetGrantvestmentInteraction
): Promise<PaginatedEntityList<GrantVestmentInteraction>> =>
  apiHttpClient.get(`grantvestment-interaction`, {
    params: {
      offset: getGrantvestmentInteractionInput.offset,
      limit: getGrantvestmentInteractionInput.limit,
      filter: {
        grantvestmentId:
          getGrantvestmentInteractionInput.filter.grantvestmentId,
        userId: getGrantvestmentInteractionInput.filter.userId,
      },
    },
  });

export interface GetGrantvestmentFollower {
  offset?: number;
  limit?: number;
  filter: {
    grantvestmentId: number;
    userId: number;
  };
}

export const getGrantvestmentFollowerAPI = (
  getGrantvestmentFollowerInput: GetGrantvestmentFollower
): Promise<PaginatedEntityList<GrantVestmentFollower>> =>
  apiHttpClient.get(`grantvestment-follower`, {
    params: {
      offset: getGrantvestmentFollowerInput.offset,
      limit: getGrantvestmentFollowerInput.limit,
      filter: {
        grantvestmentId: getGrantvestmentFollowerInput.filter.grantvestmentId,
        userId: getGrantvestmentFollowerInput.filter.userId,
      },
    },
  });

export const deleteGrantvestmentInteractionAPI = (
  interactionId: number
): Promise<any> =>
  apiHttpClient.delete(`grantvestment-interaction/${interactionId}`);

export const deleteGrantvestmentFollowerAPI = (
  followerId: number
): Promise<any> => apiHttpClient.delete(`grantvestment-follower/${followerId}`);
